/**
 * M7 S11: echoes and callbacks (spec §1 rule 7, §4–§8 echo/callback rows, §8.3 slanted-outlet items, §16.6).
 *
 * Every consequence gets an **echo** now and a **callback** later (`callbacks.json`). An entry is keyed on a story
 * flag (scheduled when the flag goes from unset to set) or a story trigger (`STORY_TRIGGERS`: the ladder moves and
 * the reactivation events, scheduled by their owner with `scheduleCallbacks`). Each one becomes an S6 `callback`
 * countdown `cb:<callbackId>` (plus `:<key>` for per-station triggers), so delays count **online** faction days and
 * never run during a catch-up. Mission echoes (`missions/*.json echo`) use the same `cb:<id>` countdowns.
 *
 * When one fires (`fireCallback`, the `callback` handler for `tickOnlineDay`), its `condition` is checked against
 * the Career as it is then; a Wire entry posts through the worker's Wire (`postWire`, with its outlet), a comms or
 * log entry becomes a `callback` effect (a one-line hail or a texture record). A callback is **never evidence**: it
 * adds no log, lead, proof, flag or tier (T33: boot-log and reactivation callbacks never unlock a required lead).
 *
 * Pure (Career in, Career out); event-driven, never per frame.
 */
import type { Career } from "../career/types";
import { logger } from "../core/logger";
import {
  countdownOf,
  scheduleCountdown,
  type CountdownHandler,
  type TickOptions,
} from "./countdowns";
import { storyData } from "./data";
import type { StoryCallback } from "./schemas";
import { hasFlag, storyOf } from "./state";
import {
  FINALE_PATHS,
  STORY_FLAGS,
  STORY_TRIGGERS,
  type FinalePath,
  type StoryCommand,
  type StoryCountdown,
  type StoryEffect,
  type StoryFlag,
  type StoryResult,
  type StoryTrigger,
} from "./types";

export type CallbackTrigger = StoryFlag | StoryTrigger;

/** The countdown id prefix S6 uses for mission echoes too. */
export const CALLBACK_PREFIX = "cb:";

// ---------------------------------------------------------------------------------------------
// Context: what a trigger knows (a station's FR name, the marked family, a ladder move)
// ---------------------------------------------------------------------------------------------

/** Text tokens a trigger can fill (`{station}`, `{family}`); flags fill none. */
export const CALLBACK_TOKENS = ["station", "family"] as const;
export type CallbackToken = (typeof CALLBACK_TOKENS)[number];

export interface CallbackVars {
  /** The restarted station's FR-### name. */
  station?: string;
  /** The Holdfast family named on a marked site's survey card. */
  family?: string;
  /** A ladder move's or a commit's paths. */
  from?: FinalePath;
  to?: FinalePath;
}

export interface TriggerContext {
  /** Makes the countdown per-instance (a POI id): `cb:<id>:<key>`. Absent = once per career while pending. */
  key?: string;
  vars?: CallbackVars;
}

/** Which tokens each trigger provides (a text using another token is a data error). */
const TRIGGER_TOKENS: Readonly<
  Partial<Record<CallbackTrigger, readonly CallbackToken[]>>
> = {
  restartLit: ["station"],
  handoverHoldfast: ["station", "family"],
  handoverTethysVein: ["station"],
  markedSelfRestart: ["station", "family"],
  okaforLabRestart: ["station"],
};

const PATHS: ReadonlySet<string> = new Set(FINALE_PATHS);

function asPath(v: string | null): FinalePath | undefined {
  return v !== null && PATHS.has(v) ? (v as FinalePath) : undefined;
}

/** `<callbackId>` or `<callbackId>?station=…&family=…&from=…&to=…` (the countdown's `ref`). */
export function callbackRef(
  callbackId: string,
  vars: CallbackVars = {},
): string {
  const q = new URLSearchParams();
  if (vars.station !== undefined) q.set("station", vars.station);
  if (vars.family !== undefined) q.set("family", vars.family);
  if (vars.from !== undefined) q.set("from", vars.from);
  if (vars.to !== undefined) q.set("to", vars.to);
  const s = q.toString();
  return s.length === 0 ? callbackId : `${callbackId}?${s}`;
}

export function parseCallbackRef(ref: string): {
  callbackId: string;
  vars: CallbackVars;
} {
  const at = ref.indexOf("?");
  if (at < 0) return { callbackId: ref, vars: {} };
  const q = new URLSearchParams(ref.slice(at + 1));
  const vars: CallbackVars = {};
  const station = q.get("station");
  const family = q.get("family");
  const from = asPath(q.get("from"));
  const to = asPath(q.get("to"));
  if (station !== null) vars.station = station;
  if (family !== null) vars.family = family;
  if (from !== undefined) vars.from = from;
  if (to !== undefined) vars.to = to;
  return { callbackId: ref.slice(0, at), vars };
}

export function callbackCountdownId(callbackId: string, key?: string): string {
  return key === undefined
    ? `${CALLBACK_PREFIX}${callbackId}`
    : `${CALLBACK_PREFIX}${callbackId}:${key}`;
}

// ---------------------------------------------------------------------------------------------
// Conditions (checked when the callback fires)
// ---------------------------------------------------------------------------------------------

type ConditionFn = (career: Career, vars: CallbackVars) => boolean;

const finalePaths = (c: Career): readonly FinalePath[] =>
  storyOf(c).finale.paths;

const CONDITIONS = {
  /** M1: the recorder went to the Mutual, not to Tethys (the "decoded only" echo, §4). */
  manifestDecoded: (c) =>
    hasFlag(c, "manifest_recovered") && !hasFlag(c, "manifest_sold"),
  /** From tier 3 the Wire names the raiders "guardians" (§5). */
  tier3: (c) => (c.storyTier ?? 0) >= 3,
  belowTier3: (c) => (c.storyTier ?? 0) < 3,
  okaforSafe: (c) => storyOf(c).okaforState === "safe",
  okaforHideout: (c) => storyOf(c).okaforState === "hideout",
  /** Okafor can still talk to the player (not held, not lost). */
  okaforFree: (c) => {
    const s = storyOf(c).okaforState;
    return s === "hideout" || s === "aboard" || s === "safe";
  },
  reyesAlive: (c) => storyOf(c).reyesState !== "dead",
  haleKnows: (c) => hasFlag(c, "hale_knows"),
  haleUnaware: (c) => !hasFlag(c, "hale_knows"),
  duarteHidden: (c) => !hasFlag(c, "duarte_exposed"),
  haleDealStands: (c) =>
    hasFlag(c, "hale_deal") && !hasFlag(c, "hale_deal_voided"),
  /** Blackmail is still the path in force (never left). */
  stillBlackmail: (c) =>
    finalePaths(c).includes("blackmail") &&
    storyOf(c).finale.from !== "blackmail",
  /** Ladder and commit contexts. */
  leftBlackmail: (_c, v) => v.from === "blackmail",
  toExpose: (_c, v) => v.to === "expose",
  toGuardians: (_c, v) => v.to === "guardians",
  toSell: (_c, v) => v.to === "sell",
  /** Expose and Guardians both in force after the move ("the ban's sponsor flies with raiders", §8.3). */
  exposeAndGuardians: (c) =>
    finalePaths(c).includes("expose") && finalePaths(c).includes("guardians"),
} as const satisfies Record<string, ConditionFn>;

export type CallbackCondition = keyof typeof CONDITIONS;
export const CALLBACK_CONDITIONS = Object.keys(
  CONDITIONS,
) as CallbackCondition[];

function isCondition(v: string): v is CallbackCondition {
  return Object.hasOwn(CONDITIONS, v);
}

/** Whether a callback's condition holds now (an unknown condition never holds; `callbackProblems` flags it). */
export function conditionHolds(
  career: Career,
  condition: string | undefined,
  vars: CallbackVars = {},
): boolean {
  if (condition === undefined) return true;
  if (!isCondition(condition)) return false;
  return CONDITIONS[condition](career, vars);
}

// ---------------------------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------------------------

/** FNV-1a (deterministic delay picks: no `Math.random` in story logic). */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The online-day delay for a countdown: `delayDays` `[min, max]`, picked from the career seed and the id. */
export function pickDelay(
  career: Career,
  cb: StoryCallback,
  countdownId: string,
): number {
  const [lo, hi] = cb.delayDays;
  if (hi === lo) return lo;
  return (
    lo +
    (hash(`${career.seed}:${countdownId}:${storyOf(career).onlineDays}`) %
      (hi - lo + 1))
  );
}

export function callbacksFor(
  trigger: CallbackTrigger,
  callbacks: readonly StoryCallback[] = storyData().callbacks,
): StoryCallback[] {
  return callbacks.filter((cb) => cb.flag === trigger);
}

/**
 * Schedules every echo and callback keyed on `trigger` as `callback` countdowns. A countdown already pending with
 * the same id keeps its due day (a mission echo and the flag that sets it converge on one countdown).
 */
export function scheduleCallbacks(
  career: Career,
  trigger: CallbackTrigger,
  ctx: TriggerContext = {},
  callbacks: readonly StoryCallback[] = storyData().callbacks,
): Career {
  let next = career;
  for (const cb of callbacksFor(trigger, callbacks)) {
    const id = callbackCountdownId(cb.id, ctx.key);
    if (countdownOf(next, id) !== undefined) continue;
    next = scheduleCountdown(next, {
      id,
      kind: "callback",
      inOnlineDays: pickDelay(next, cb, id),
      ref: callbackRef(cb.id, ctx.vars),
    });
  }
  return next;
}

/** Flags set in `after` that weren't in `before`, in `STORY_FLAGS` order. */
export function newlySetFlags(before: Career, after: Career): StoryFlag[] {
  return STORY_FLAGS.filter((f) => hasFlag(after, f) && !hasFlag(before, f));
}

/** Schedules the callbacks of every flag that went from unset to set between `before` and `after` (S30 calls it). */
export function scheduleNewFlagCallbacks(
  before: Career,
  after: Career,
  callbacks: readonly StoryCallback[] = storyData().callbacks,
): Career {
  let next = after;
  for (const flag of newlySetFlags(before, after))
    next = scheduleCallbacks(next, flag, {}, callbacks);
  return next;
}

// ---------------------------------------------------------------------------------------------
// Firing
// ---------------------------------------------------------------------------------------------

/** Fills `{station}` and `{family}` from the trigger's vars (a token without a value stays as written). */
export function fillCallbackText(text: string, vars: CallbackVars): string {
  return text.replace(
    /\{(station|family)\}/g,
    (whole, token: CallbackToken) => vars[token] ?? whole,
  );
}

/** What a fired callback sends and shows (no Career change: a callback is never evidence, T33). */
export function callbackOutput(
  cb: StoryCallback,
  vars: CallbackVars,
): { commands: StoryCommand[]; effects: StoryEffect[] } {
  const text = fillCallbackText(cb.text, vars);
  if (cb.channel === "wire") {
    if (cb.source === undefined || cb.severity === undefined) {
      logger.warn(
        `story: Wire callback ${cb.id} has no outlet or severity; skipped`,
      );
      return { commands: [], effects: [] };
    }
    return {
      commands: [
        {
          type: "postWire",
          headline: text,
          severity: cb.severity,
          source: cb.source,
        },
      ],
      effects: [],
    };
  }
  const effect: StoryEffect = {
    type: "callback",
    callbackId: cb.id,
    channel: cb.channel,
    text,
  };
  if (cb.speaker !== undefined) effect.speaker = cb.speaker;
  return { commands: [], effects: [effect] };
}

/**
 * The `callback` countdown handler (`tickOnlineDay`): looks the callback up by the countdown's ref, checks its
 * condition against the Career now, and returns its Wire command or its comms/log effect. Unknown ids are logged
 * and dropped. The Career is returned unchanged.
 */
export function fireCallback(
  career: Career,
  fired: StoryCountdown,
  callbacks: readonly StoryCallback[] = storyData().callbacks,
): StoryResult<Career> {
  const { callbackId, vars } = parseCallbackRef(
    fired.ref ?? fired.id.slice(CALLBACK_PREFIX.length),
  );
  const cb = callbacks.find((c) => c.id === callbackId);
  if (cb === undefined) {
    logger.warn(
      `story: countdown ${fired.id} names no callback '${callbackId}'`,
    );
    return { career, commands: [], effects: [] };
  }
  if (!conditionHolds(career, cb.condition, vars))
    return { career, commands: [], effects: [] };
  return { career, ...callbackOutput(cb, vars) };
}

/** The handler map for `tickOnlineDay` (S30 merges it with S13's and S21's). */
export function callbackHandlers(
  callbacks: readonly StoryCallback[] = storyData().callbacks,
): TickOptions["handlers"] {
  const callback: CountdownHandler = (career, fired) =>
    fireCallback(career, fired, callbacks);
  return { callback };
}

// ---------------------------------------------------------------------------------------------
// Content checks (the shipped file and tests)
// ---------------------------------------------------------------------------------------------

const TRIGGERS: ReadonlySet<string> = new Set<string>(STORY_TRIGGERS);

/** Content problems beyond the S0 loader's: conditions, tokens, Wire severity, comms speakers. Empty = fine. */
export function callbackProblems(
  callbacks: readonly StoryCallback[],
): string[] {
  const out: string[] = [];
  for (const cb of callbacks) {
    if (cb.condition !== undefined && !isCondition(cb.condition))
      out.push(`${cb.id}: unknown condition '${cb.condition}'`);
    if (cb.channel === "wire" && cb.severity === undefined)
      out.push(`${cb.id}: a Wire callback sets its severity`);
    if (cb.channel === "comms" && cb.speaker === undefined)
      out.push(`${cb.id}: a comms callback names its speaker`);
    if (cb.channel !== "wire" && cb.source !== undefined)
      out.push(`${cb.id}: only a Wire callback has an outlet`);
    if (cb.text.trim().length === 0) out.push(`${cb.id}: empty text`);
    const allowed: readonly string[] = TRIGGERS.has(cb.flag)
      ? (TRIGGER_TOKENS[cb.flag as StoryTrigger] ?? [])
      : [];
    for (const m of cb.text.matchAll(/\{([a-zA-Z]+)\}/g)) {
      const token = m[1] ?? "";
      if (!allowed.includes(token))
        out.push(`${cb.id}: token {${token}} isn't filled by '${cb.flag}'`);
    }
  }
  return out;
}
